import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  LinearProgress,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Button
} from '@mui/material'
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material'
import {
  getGroupCreationStatus,
  GroupCreationProgressResponse,
  startAssignOwnershipPass3,
  startUpdateGroupPropertiesPass4
} from '../services/api'

interface GroupCreationProgressModalProps {
  open: boolean
  sessionId: string | null
  tenantId: string
  onComplete: (success: boolean) => void
  onError: (error: string) => void
}

const PASS_NAMES: Record<number, string> = {
  1: 'Creating License Groups in Tenant',
  2: 'Creating Groups in Tenant',
  3: 'Assigning Ownership to Created Groups',
  4: 'Configuring Group Features',
  5: 'Reprocessing Stuck Groups'
}

export default function GroupCreationProgressModal({
  open,
  sessionId,
  tenantId,
  onComplete,
  onError
}: GroupCreationProgressModalProps) {
  const [status, setStatus] = useState<GroupCreationProgressResponse | null>(null)
  const [pollingError, setPollingError] = useState<string | null>(null)
  const [noUpdateCount, setNoUpdateCount] = useState(0)
  const [pass3Triggered, setPass3Triggered] = useState(false)
  const [pass4Triggered, setPass4Triggered] = useState(false)

  useEffect(() => {
    if (!open || !sessionId) {
      setStatus(null)
      setPollingError(null)
      setNoUpdateCount(0)
      setPass3Triggered(false)
      setPass4Triggered(false)
      return
    }

    let lastStatus: GroupCreationProgressResponse | null = null
    let pollCount = 0
    let consecutiveNoUpdates = 0

    const pollStatus = async () => {
      try {
        const response = await getGroupCreationStatus(tenantId, sessionId)
        pollCount++

        // Check if status is stuck (no updates for 60 seconds)
        if (lastStatus && JSON.stringify(lastStatus) === JSON.stringify(response)) {
          consecutiveNoUpdates++
          setNoUpdateCount(consecutiveNoUpdates)

          if (consecutiveNoUpdates >= 15) { // 15 * 2 seconds = 30 seconds of no updates
            setPollingError('Group creation is taking longer than expected. Please check status manually.')
            onError('Group creation timeout')
            return
          }
        } else {
          consecutiveNoUpdates = 0
          setNoUpdateCount(0)
        }

        lastStatus = response
        setStatus(response)
        setPollingError(null)

        // Stop polling if complete or failed
        if (response.isComplete) {
          if (response.status === 'Completed' || response.status === 'PartialSuccess') {
            onComplete(true)
          } else if (response.status === 'Failed') {
            onError(response.statusMessage)
            onComplete(false)
          }
        }
      } catch (err: any) {
        setPollingError(err.message || 'Failed to fetch group creation status')
        onError(err.message || 'Failed to fetch group creation status')
      }
    }

    // Initial poll
    pollStatus()

    // Poll every 2 seconds for responsive UI updates (batch API returns quickly)
    const interval = setInterval(pollStatus, 2000)

    return () => clearInterval(interval)
  }, [open, sessionId, tenantId, onComplete, onError])

  useEffect(() => {
    if (!status || !sessionId || status.isComplete) {
      return
    }

    if (status.currentPass === 3 && !pass3Triggered) {
      setPass3Triggered(true)
      startAssignOwnershipPass3(tenantId, sessionId).catch(err => {
        setPass3Triggered(false)
        const message = err?.message || 'Failed to start ownership assignment'
        setPollingError(message)
        onError(message)
      })
    }

    if (status.currentPass === 4 && !pass4Triggered) {
      setPass4Triggered(true)
      startUpdateGroupPropertiesPass4(tenantId, sessionId).catch(err => {
        setPass4Triggered(false)
        const message = err?.message || 'Failed to start property updates'
        setPollingError(message)
        onError(message)
      })
    }
  }, [status, tenantId, sessionId, pass3Triggered, pass4Triggered, onError])

  if (!status) {
    return (
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>Creating Groups</DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" justifyContent="center" py={4} gap={2}>
            <CircularProgress />
            <Typography>Initializing group creation...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  const progressPercentage = status.progress?.progressPercentage || 0
  const isComplete = status.isComplete
  const isFailed = status.status === 'Failed'

  return (
    <Dialog open={open} maxWidth="md" fullWidth disableEscapeKeyDown={!isComplete}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {isFailed && <ErrorIcon color="error" />}
          {isComplete && !isFailed && <SuccessIcon color="success" />}
          {!isComplete && <CircularProgress size={24} />}
          <Typography variant="h6" component="span">
            Group Creation Progress
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Heads up</AlertTitle>
          Group creation may take a few minutes; please wait while we verify and configure required groups.
        </Alert>
        <Box display="flex" flexDirection="column" gap={3} py={2}>
          {/* Pass Information */}
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight="bold" color="textSecondary">
                Current Pass
              </Typography>
              <Chip
                label={PASS_NAMES[status.currentPass] || `Pass ${status.currentPass}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="textSecondary">
                Progress
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {progressPercentage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          {/* Group Status */}
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Groups Processed
            </Typography>
            <Typography variant="h6">
              {status.progress?.currentGroupNumber || 0} of {status.progress?.totalGroupsToCreate || 0}
            </Typography>
          </Box>

          {/* Current Status Message */}
          {status.statusMessage && (
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Status
              </Typography>
              <Typography variant="body2">{status.statusMessage}</Typography>
            </Box>
          )}

          {/* Error Messages */}
          {isFailed && (
            <Alert severity="error">
              <AlertTitle>Group Creation Failed</AlertTitle>
              {status.statusMessage || 'An error occurred during group creation. Please try again.'}
            </Alert>
          )}

          {pollingError && !isComplete && (
            <Alert severity="warning">
              <AlertTitle>Status Update Issue</AlertTitle>
              {pollingError}
              {noUpdateCount > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  No updates received for {noUpdateCount * 2} seconds
                </Typography>
              )}
            </Alert>
          )}

          {status.status === 'PartialSuccess' && isComplete && (
            <Alert severity="warning">
              <AlertTitle>Partial Success</AlertTitle>
              Some groups were created successfully, but not all. Please check the results.
            </Alert>
          )}

          {status.status === 'Completed' && isComplete && (
            <Alert severity="success">
              <AlertTitle>All Groups Created Successfully</AlertTitle>
              Group creation completed in all passes.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {}}
          disabled={!isComplete}
          variant="contained"
          color={isFailed ? 'error' : 'success'}
        >
          {isFailed ? 'Retry' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
